import { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import useCanvasActivity from "@/hooks/useCanvasActivity";

const RED_CONTOUR = [
  [-1.1338, 1.2102], [0.8025, 1.2102], [1.0064, 0.8790],
  [0.4459, -0.0510], [-0.1401, -0.0510], [-0.7643, -1.0318],
  [-1.1975, -1.0191], [-0.3439, 0.3439], [0.2420, 0.3439],
  [0.5478, 0.8408], [-0.9045, 0.8408],
];

const WHITE_CONTOUR = [
  [0.7771, 0.2420], [1.6178, -1.0446], [1.1720, -1.0446],
  [0.8662, -0.5605], [0.2420, -0.5605], [0.1656, -0.4459],
  [0.0255, -0.2038], [0.6369, -0.2038], [0.5732, -0.0764],
];

function createShape(points) {
  const shape = new THREE.Shape();
  shape.moveTo(points[0][0], points[0][1]);
  points.slice(1).forEach(([x, y]) => shape.lineTo(x, y));
  shape.closePath();
  return shape;
}

function LogoPiece({ points, color, sideColor }) {
  const geometry = useMemo(() => new THREE.ExtrudeGeometry(createShape(points), {
    depth: 0.24,
    steps: 1,
    bevelEnabled: true,
    bevelSegments: 3,
    bevelSize: 0.022,
    bevelThickness: 0.022,
    curveSegments: 1,
  }), [points]);

  const materials = useMemo(() => [
    new THREE.MeshStandardMaterial({ color, metalness: 0.34, roughness: 0.24 }),
    new THREE.MeshStandardMaterial({ color: sideColor, metalness: 0.44, roughness: 0.34 }),
  ], [color, sideColor]);

  useEffect(() => () => {
    geometry.dispose();
    materials.forEach((material) => material.dispose());
  }, [geometry, materials]);

  return <mesh geometry={geometry} material={materials} castShadow />;
}

function LogoMark({
  scale = 0.58,
  primaryColor = "#f3001a",
  primarySideColor = "#520009",
  secondaryColor = "#ffffff",
  secondarySideColor = "#686872",
}) {
  return (
    <group scale={scale} position={[0, 0, -0.1]}>
      <LogoPiece points={RED_CONTOUR} color={primaryColor} sideColor={primarySideColor} />
      <LogoPiece points={WHITE_CONTOUR} color={secondaryColor} sideColor={secondarySideColor} />
    </group>
  );
}

const CORE_NODES = [
  [1.28, 0.1, 0], [-1.2, -0.1, 0], [0.1, 1.02, 0], [-0.08, -1.02, 0],
];

function CoreLink({ position, color }) {
  const geometry = useMemo(() => new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0, -0.08),
    new THREE.Vector3(position[0], position[1], -0.08),
  ]), [position]);

  useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <line geometry={geometry}>
      <lineBasicMaterial color={color} transparent opacity={0.36} />
    </line>
  );
}

function RaavaCore() {
  const rig = useRef();
  const orbit = useRef();

  useFrame((state, delta) => {
    rig.current.rotation.y = THREE.MathUtils.lerp(rig.current.rotation.y, state.pointer.x * 0.55, 0.055);
    rig.current.rotation.x = THREE.MathUtils.lerp(rig.current.rotation.x, -state.pointer.y * 0.4, 0.055);
    orbit.current.rotation.z += delta * 0.12;
    orbit.current.rotation.y += delta * 0.08;
  });

  return (
    <group ref={rig}>
      <LogoMark scale={0.62} />
      <group ref={orbit}>
        {CORE_NODES.map((position, index) => (
          <group key={position.join(":")}>
            <CoreLink position={position} color={index % 2 ? "#ffffff" : "#f00018"} />
            <mesh position={position}>
              <icosahedronGeometry args={[0.14, 1]} />
              <meshStandardMaterial
                color={index % 2 ? "#ffffff" : "#f00018"}
                emissive={index % 2 ? "#333333" : "#7a000d"}
                emissiveIntensity={0.7}
                wireframe={index === 2}
              />
            </mesh>
          </group>
        ))}
      </group>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0.08, 0, -0.34]}>
        <torusGeometry args={[1.45, 0.008, 6, 96]} />
        <meshBasicMaterial color="#f00018" transparent opacity={0.25} />
      </mesh>
    </group>
  );
}

function ForgeAssembly() {
  const rig = useRef();
  const red = useRef();
  const white = useRef();
  const sparks = useRef();
  const sparkPositions = useMemo(() => {
    const result = new Float32Array(72 * 3);
    for (let index = 0; index < 72; index += 1) {
      const radius = 0.7 + Math.random() * 1.2;
      const angle = Math.random() * Math.PI * 2;
      result[index * 3] = Math.cos(angle) * radius;
      result[index * 3 + 1] = Math.sin(angle) * radius;
      result[index * 3 + 2] = (Math.random() - 0.5) * 0.9;
    }
    return result;
  }, []);

  useFrame((state, delta) => {
    const time = state.clock.elapsedTime;
    const separation = (0.5 + Math.cos(time * 1.15) * 0.5) * 0.32;
    rig.current.rotation.y = THREE.MathUtils.lerp(rig.current.rotation.y, state.pointer.x * 0.65, 0.055);
    rig.current.rotation.x = THREE.MathUtils.lerp(rig.current.rotation.x, -state.pointer.y * 0.45, 0.055);
    red.current.position.set(-separation, separation * 0.18, separation * 0.45);
    white.current.position.set(separation, -separation * 0.2, -separation * 0.25);
    sparks.current.rotation.z -= delta * 0.18;
    sparks.current.rotation.y += delta * 0.1;
  });

  return (
    <group ref={rig}>
      <group ref={red} scale={0.68}>
        <LogoPiece points={RED_CONTOUR} color="#ff001c" sideColor="#590009" />
      </group>
      <group ref={white} scale={0.68}>
        <LogoPiece points={WHITE_CONTOUR} color="#ffffff" sideColor="#6a6a74" />
      </group>
      <points ref={sparks}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[sparkPositions, 3]} />
        </bufferGeometry>
        <pointsMaterial color="#ff273e" size={0.035} transparent opacity={0.78} depthWrite={false} />
      </points>
      <mesh position={[0.05, 0, -0.42]}>
        <ringGeometry args={[1.35, 1.37, 96]} />
        <meshBasicMaterial color="#f00018" transparent opacity={0.22} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function CoreForgeFusion() {
  const rig = useRef();
  const orbit = useRef();
  const red = useRef();
  const white = useRef();
  const sparks = useRef();
  const sparkPositions = useMemo(() => {
    const result = new Float32Array(54 * 3);
    for (let index = 0; index < 54; index += 1) {
      const radius = 0.78 + Math.random() * 0.86;
      const angle = Math.random() * Math.PI * 2;
      result[index * 3] = Math.cos(angle) * radius;
      result[index * 3 + 1] = Math.sin(angle) * radius * 0.78;
      result[index * 3 + 2] = (Math.random() - 0.5) * 0.62;
    }
    return result;
  }, []);

  useFrame((state, delta) => {
    const time = state.clock.elapsedTime;
    const separation = 0.045 + (0.5 + Math.sin(time * 1.35) * 0.5) * 0.075;
    red.current.position.set(-separation, separation * 0.14, separation * 0.34);
    white.current.position.set(separation, -separation * 0.16, -separation * 0.2);
    orbit.current.rotation.z += delta * 0.1;
    orbit.current.rotation.y += delta * 0.06;
    sparks.current.rotation.z -= delta * 0.13;
    sparks.current.rotation.y += delta * 0.08;
  });

  return (
    <group ref={rig}>
      <group ref={red} scale={0.64}>
        <LogoPiece points={RED_CONTOUR} color="#ff001c" sideColor="#550008" />
      </group>
      <group ref={white} scale={0.64}>
        <LogoPiece points={WHITE_CONTOUR} color="#ffffff" sideColor="#686872" />
      </group>

      <group ref={orbit} position={[0, 0, -0.14]}>
        {CORE_NODES.map((position, index) => (
          <group key={`fusion-${position.join(":")}`}>
            <CoreLink position={position} color={index % 2 ? "#ffffff" : "#f00018"} />
            <mesh position={position}>
              {index % 2 === 0 ? (
                <octahedronGeometry args={[0.13, 0]} />
              ) : (
                <icosahedronGeometry args={[0.13, 1]} />
              )}
              <meshStandardMaterial
                color={index % 2 ? "#ffffff" : "#f00018"}
                emissive={index % 2 ? "#292929" : "#80000e"}
                emissiveIntensity={0.82}
                metalness={0.45}
                roughness={0.28}
              />
            </mesh>
          </group>
        ))}
      </group>

      <points ref={sparks}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[sparkPositions, 3]} />
        </bufferGeometry>
        <pointsMaterial color="#ff243b" size={0.028} transparent opacity={0.7} depthWrite={false} />
      </points>

      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0.06, 0, -0.38]}>
        <torusGeometry args={[1.44, 0.009, 6, 96]} />
        <meshBasicMaterial color="#f00018" transparent opacity={0.28} />
      </mesh>
      <mesh position={[0.04, 0, -0.44]}>
        <ringGeometry args={[0.82, 0.86, 96]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.08} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function ManifestPulseCore() {
  const rig = useRef();
  const core = useRef();
  const innerShell = useRef();
  const outerShell = useRef();
  const orbit = useRef();
  const echoes = useRef();
  const packets = useRef();

  useFrame((state, delta) => {
    const time = state.clock.elapsedTime;
    const pulse = 1 + Math.sin(time * 2.45) * 0.16;
    const shellPulse = 1 + Math.sin(time * 2.45 - 0.7) * 0.035;

    core.current.scale.setScalar(pulse);
    core.current.rotation.y += delta * 0.72;
    core.current.rotation.x += delta * 0.22;
    innerShell.current.rotation.y -= delta * 0.28;
    innerShell.current.rotation.z += delta * 0.12;
    innerShell.current.scale.setScalar(shellPulse);
    outerShell.current.rotation.x += delta * 0.08;
    outerShell.current.rotation.y += delta * 0.16;
    orbit.current.rotation.z -= delta * 0.12;
    orbit.current.rotation.y += delta * 0.08;

    echoes.current.children.forEach((echo, index) => {
      const progress = (time * 0.36 + index / echoes.current.children.length) % 1;
      const size = 0.45 + progress * 1.65;
      echo.scale.setScalar(size);
      echo.material.opacity = Math.sin(progress * Math.PI) * 0.25;
    });

    packets.current.children.forEach((packet, index) => {
      const angle = time * (0.55 + index * 0.08) + index * ((Math.PI * 2) / 3);
      const radius = 1.14 + Math.sin(time * 1.2 + index) * 0.08;
      packet.position.set(
        Math.cos(angle) * radius,
        Math.sin(angle) * radius * 0.72,
        Math.sin(angle * 1.4 + index) * 0.42,
      );
    });
  });

  return (
    <group ref={rig}>
      <group ref={core} position={[0, 0, 0.12]}>
        <mesh>
          <icosahedronGeometry args={[0.58, 0]} />
          <meshBasicMaterial
            color="#00E5FF"
            wireframe
            transparent
            opacity={0.98}
          />
        </mesh>
        <mesh scale={0.58}>
          <icosahedronGeometry args={[0.58, 0]} />
          <meshBasicMaterial
            color="#00E5FF"
            transparent
            opacity={0.13}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
        <LogoMark
          scale={0.2}
          primaryColor="#00E5FF"
          primarySideColor="#004852"
          secondaryColor="#ffffff"
          secondarySideColor="#52636a"
        />
      </group>

      <mesh ref={innerShell} position={[0, 0, 0.02]}>
        <icosahedronGeometry args={[0.96, 1]} />
        <meshBasicMaterial color="#ffffff" wireframe transparent opacity={0.2} />
      </mesh>
      <mesh ref={outerShell} position={[0, 0, -0.08]}>
        <icosahedronGeometry args={[1.34, 1]} />
        <meshBasicMaterial color="#00E5FF" wireframe transparent opacity={0.32} />
      </mesh>

      <group ref={echoes} position={[0, 0, -0.18]}>
        {[0, 1, 2].map((index) => (
          <mesh key={`pulse-echo-${index}`}>
            <ringGeometry args={[0.72, 0.735, 96]} />
            <meshBasicMaterial
              color={index === 1 ? "#ffffff" : "#00E5FF"}
              transparent
              opacity={0.18}
              side={THREE.DoubleSide}
              depthWrite={false}
              blending={THREE.AdditiveBlending}
            />
          </mesh>
        ))}
      </group>

      <group ref={orbit} position={[0, 0, -0.12]}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[1.5, 0.008, 6, 96]} />
          <meshBasicMaterial color="#00E5FF" transparent opacity={0.36} />
        </mesh>
        <mesh rotation={[Math.PI / 2.8, 0.18, Math.PI / 3]}>
          <torusGeometry args={[1.18, 0.008, 6, 96]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.2} />
        </mesh>
      </group>

      <group ref={packets}>
        {[0, 1, 2].map((index) => (
          <mesh key={`signal-packet-${index}`}>
            <octahedronGeometry args={[0.075, 0]} />
            <meshBasicMaterial
              color={index === 1 ? "#ffffff" : "#00E5FF"}
              wireframe={index === 2}
            />
          </mesh>
        ))}
      </group>
    </group>
  );
}

const CONCEPTS = {
  fusion: CoreForgeFusion,
  reactive: ManifestPulseCore,
  core: RaavaCore,
  forge: ForgeAssembly,
};

export default function ManifestConceptScene({ concept }) {
  const Concept = CONCEPTS[concept] || CoreForgeFusion;
  const lightColor = concept === "reactive" ? "#00E5FF" : "#f00018";
  const { hostRef, frameloop } = useCanvasActivity();
  return (
    <div ref={hostRef} className="pointer-events-none h-52 sm:h-60 w-full" data-testid={`manifest-concept-${concept}`}>
      <Canvas
        camera={{ position: [0, 0, 3.65], fov: 44 }}
        frameloop={frameloop}
        gl={{ alpha: true, antialias: true, powerPreference: "high-performance" }}
        dpr={[1, 1.2]}
      >
        <ambientLight intensity={0.7} />
        <directionalLight position={[3, 4, 5]} intensity={2.6} color="#ffffff" />
        <pointLight position={[-2, -1, 2]} intensity={12} distance={5} color={lightColor} />
        <Concept />
      </Canvas>
    </div>
  );
}
